import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { OperationStructuredType } from "../../../app/model/domain/OperationStructuredType";
import { PagedList } from "../../../app/model/domain/PagedList";
import { StructuredOperationFileModel } from "../../../app/model/domain/StructuredOperationFileModel";
import { StructuredOperationModel } from "../../../app/model/domain/StructuredOperationModel";
import { environment } from "../../../environments/environment";
import { ApprovedAssetsModel } from "../../model/domain/ApprovedAssetsModel";
import { ReviewersDecisionModel } from "../../model/domain/ReviewersDecisionModel";
import { StructuredOperationApproveReservationModel } from "../../model/domain/StructuredOperationApproveReservationModel";
import { StructuredOperationPrecedentModel } from "../../model/domain/StructuredOperationPrecedentModel";
import { FilterDateType } from "./../../model/domain/MonitorFilter";
import { ITicketableService, StatusDetails } from "./ITicketableService";

@Injectable({
  providedIn: "root",
})
export class StructuredOperationsService
  implements ITicketableService<StructuredOperationModel>
{
  private readonly name = "structured";

  constructor(private http: HttpClient) {}

  updateTicket(id: number, ticket: StructuredOperationModel): Observable<any> {
    return this.http.put(
      `${environment.apiUrls.tickets}/${this.name}/${id}`,
      ticket
    );
  }

  getTicket(id: number): Observable<StructuredOperationModel> {
    return this.http.get<StructuredOperationModel>(
      `${environment.apiUrls.tickets}/${this.name}/${id}`
    );
  }

  getReviewersDecision(ticketId: number): Observable<ReviewersDecisionModel[]> {
    return this.http.get<ReviewersDecisionModel[]>(
      `${environment.apiUrls.tickets}/${this.name}/get-reviewers-decision/${ticketId}`
    );
  }

  createPrecedent(precedent: StructuredOperationPrecedentModel) {
    return this.http.post<StructuredOperationPrecedentModel>(
      `${environment.apiUrls.tickets}/${this.name}/create-precedent`,
      precedent
    );
  }

  getAllPrecedent(
    ticketId: number
  ): Observable<StructuredOperationPrecedentModel[]> {
    return this.http.get<StructuredOperationPrecedentModel[]>(
      `${environment.apiUrls.tickets}/${this.name}/get-all-precedent/${ticketId}`
    );
  }

  createApprovedAsset(model: ApprovedAssetsModel) {
    return this.http.post<ApprovedAssetsModel>(
      `${environment.apiUrls.tickets}/${this.name}/create-approved-asset`,
      model
    );
  }

  getApprovedAssets(ticketId: number): Observable<ApprovedAssetsModel[]> {
    return this.http.get<ApprovedAssetsModel[]>(
      `${environment.apiUrls.tickets}/${this.name}/get-approved-assets/${ticketId}`
    );
  }

  listTicket(
    dateType: FilterDateType = null,
    startDate: Date = null,
    endDate: Date = null,
    statusId: number = null,
    operationTypeId: number = null,
    settlementTypeId: number = null,
    portfolioCode: string = null,
    portfolioName: string = null,
    portfolioDocument: string = null,
    ticketId: number = null,
    accountManagerId: number = null,
    page: number = null,
    pageSize: number = null,
    isOutstanding: boolean = false
  ) {
    const params: any = { isOutstanding: isOutstanding };

    if (dateType) {
      params.dateType = dateType;
    }

    if (startDate) {
      params.startDate = startDate.toJSON();
    }

    if (endDate) {
      params.endDate = endDate.toJSON();
    }

    if (statusId) {
      params.statusId = statusId.toString();
    }

    if (operationTypeId) {
      params.operationTypeId = operationTypeId;
    }

    if (settlementTypeId) {
      params.settlementTypeId = settlementTypeId;
    }

    if (operationTypeId) {
      params.operationTypeId = operationTypeId;
    }

    if (settlementTypeId) {
      params.settlementTypeId = settlementTypeId;
    }

    if (portfolioCode) {
      params.portfolioCode = portfolioCode;
    }

    if (portfolioName) {
      params.portfolioName = portfolioName;
    }

    if (portfolioDocument) {
      params.portfolioDocument = portfolioDocument;
    }

    if (ticketId) {
      params.ticketId = ticketId;
    }

    if (accountManagerId) {
      params.accountManagerId = accountManagerId;
    }

    if (page) {
      params.page = page;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    return this.http.get<PagedList<StructuredOperationModel>>(
      `${environment.apiUrls.tickets}/${this.name}/list-paginated`,
      {
        params,
      }
    );
  }

  forwardTicket(id: number): Observable<StatusDetails> {
    return this.http.post<StatusDetails>(
      `${environment.apiUrls.tickets}/${this.name}/${id}/forward`,
      {}
    );
  }

  cancelDraftTicket(id: number): Observable<StatusDetails> {
    return this.http.post<StatusDetails>(
      `${environment.apiUrls.tickets}/${this.name}/${id}/cancel`,
      {}
    );
  }

  registerTicket(ticket: StructuredOperationModel) {
    return this.http.post(
      `${environment.apiUrls.tickets}/${this.name}`,
      ticket
    );
  }

  getOperationTypes() {
    return this.http.get<OperationStructuredType[]>(
      `${environment.apiUrls.tickets}/${this.name}/operation-types`
    );
  }

  getOperationSubTypes(id: number) {
    return this.http.get<OperationStructuredType[]>(
      `${environment.apiUrls.tickets}/${this.name}/subtype/${id}`
    );
  }

  changeStatus(statusId: number, groupId: number, ticketId: number) {
    return this.http.put(
      `${environment.apiUrls.tickets}/${this.name}/change-status/${statusId}/${groupId}`,
      ticketId
    );
  }

  getOperationSubTypeFiles(id: number) {
    return this.http.get<OperationStructuredType[]>(
      `${environment.apiUrls.tickets}/${this.name}/subtype-files/${id}`
    );
  }

  getComments(ticketId: number) {
    return this.http.get<StructuredOperationFileModel[]>(
      `${environment.apiUrls.tickets}/${this.name}/get-comments/${ticketId}`
    );
  }

  saveComments(model: StructuredOperationFileModel) {
    return this.http.post<StructuredOperationFileModel>(
      `${environment.apiUrls.tickets}/${this.name}/comment/`,
      model
    );
  }

  approveReservation(model: StructuredOperationApproveReservationModel) {
    return this.http.post(
      `${environment.apiUrls.tickets}/${this.name}/approve-reservation`,
      model
    );
  }

  saveStructuredOperationFileType(model: StructuredOperationFileModel) {
    return this.http.post<StructuredOperationFileModel>(
      `${environment.apiUrls.tickets}/${this.name}/save-operation-type-file/`,
      model
    );
  }
}
